/**
 * Площадки, где в тексте и кэф, и центы. Зеркало часто пишет 69.1 в поле
 * кэфа — это цена шара, не коэффициент 69. Иначе ROI +266% при живых 5%.
 */
import { Decimal } from 'decimal.js';

import type { Fork, ForkLeg } from './protocol';

const SHARE_VENUES = new Set([
  'polymarket',
  'poly',
  'predictfun',
  'kalshi',
  'opinion',
  'myriad',
  'limitless',
  'sxbet',
  'bxyz',
  'sharpxch',
]);

export const SHARE_ODDS_CEILING = 25;

export function isShareVenue(source: string): boolean {
  const code = source.trim().toLowerCase();
  if (code === 'poly' || code.startsWith('polymarket')) return true;
  return SHARE_VENUES.has(code);
}

export function isShareLeg(leg: {
  s?: string;
  unit?: string | null;
  book?: readonly unknown[] | null;
}): boolean {
  return (
    leg.unit === 'share' ||
    Boolean(leg.book && leg.book.length > 0) ||
    isShareVenue(leg.s ?? '')
  );
}

export function lookLikeShareCents(odds: number): boolean {
  return Number.isFinite(odds) && odds >= SHARE_ODDS_CEILING && odds < 100;
}

export function paintShareCents(leg: ForkLeg, cents: string): ForkLeg | null {
  try {
    const n = new Decimal(cents.trim().replace(',', '.'));
    if (!n.isFinite() || !n.gt(0)) return null;
    const price = n.div(100);
    if (price.lte(0) || price.gte(1)) return null;
    const odds = new Decimal(1).div(price).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
    const share = price.toDecimalPlaces(3, Decimal.ROUND_UP).toString();
    return { ...leg, unit: 'share', odds, share };
  } catch {
    return null;
  }
}

function impliedSum(fork: Fork): number | null {
  const legs = fork.legs ?? [];
  if (legs.length < 2) return null;
  let sum = 0;
  for (const leg of legs) {
    const n = Number(leg.odds);
    if (!Number.isFinite(n) || !(n > 1)) return null;
    sum += 1 / n;
  }
  return sum;
}

/** 69.1 + 4.03: 0.26 → 0.94 — берём центы. 50 + 1.03: настоящий лонгшот, не трогаем. */
export function healShareFork(fork: Fork): Fork {
  if (fork.legs === undefined) return fork;
  let changed = false;
  const legs = fork.legs.map((leg) => {
    if (!isShareLeg(leg)) return leg;
    const n = Number(leg.odds);
    if (!lookLikeShareCents(n)) return leg;
    const next = paintShareCents(leg, n.toFixed(1));
    if (next === null || next.odds === leg.odds) return leg;
    changed = true;
    return next;
  });
  if (!changed) return fork;
  const healed = { ...fork, legs, o: legs.map((one) => one.odds) };
  const before = impliedSum(fork);
  const after = impliedSum(healed);
  if (after === null) return fork;
  if (before !== null && Math.abs(after - 1) >= Math.abs(before - 1)) return fork;
  return healed;
}

/** Замок биржи — центы. 69.1 как кэф даёт +266%; 2.000 в замке — старый кэф, не 2¢. */
export function oddsFromShareLock(raw: string): string {
  try {
    const n = new Decimal(raw.trim().replace(',', '.'));
    if (!n.isFinite() || !n.gt(0)) return raw;
    if (n.lt(10)) {
      const asOdds = new Decimal(100).div(n);
      if (asOdds.gte(SHARE_ODDS_CEILING)) {
        return n.toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
      }
    }
    return new Decimal(100).div(n).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
  } catch {
    return raw;
  }
}
